'use client';
import { ReactNode, useEffect } from 'react';
import { useImpersonation } from 'app/hooks/useImpersonation';
import {
  useSessionHydration,
  useTranslationHydration,
} from 'app/hooks/useStoreHydration';
import { useTranslationStore } from 'app/stores/translationStore';
import { useToken } from 'app/utils/token';
import useRoutes from 'app/utils/useRoutes';
import Loader from 'components/Loader/loader';
import { useRouter } from 'next/navigation';

interface LoginCheckerProps {
  children: ReactNode;
  isLoginPage?: boolean;
}

let translationsRefreshed = false;

const LoginChecker: React.FC<LoginCheckerProps> = ({
  children,
  isLoginPage = false,
}) => {
  const router = useRouter();
  const ROUTES = useRoutes();
  const hasHydrated = useSessionHydration();
  const translationsHydrated = useTranslationHydration();
  const refreshTranslations = useTranslationStore(
    state => state.refreshTranslations
  );
  const { isValidToken, clearUserLoginResponse } = useToken();
  const { isImpersonating, stop } = useImpersonation();
  const isSessionValid = isValidToken();

  useEffect(() => {
    if (!hasHydrated) return;
    if (!isSessionValid) {
      if (isImpersonating) {
        void stop(true);
        return;
      }
      clearUserLoginResponse();
      if (!isLoginPage) {
        router.replace(ROUTES.home);
      }
      return;
    }
    if (isLoginPage) {
      router.replace(ROUTES.menu);
    }
  }, [hasHydrated, isSessionValid, isLoginPage, isImpersonating]);

  useEffect(() => {
    if (!hasHydrated || !translationsHydrated || !isSessionValid) return;
    if (isLoginPage || translationsRefreshed) return;
    translationsRefreshed = true;
    void refreshTranslations();
  }, [hasHydrated, translationsHydrated, isSessionValid, isLoginPage]);

  if (!hasHydrated) return <Loader />;
  if (!isLoginPage && !isSessionValid) return <Loader />;

  return <>{children}</>;
};

export default LoginChecker;
